import React, { useEffect, useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import messageIcon from "assets/img/icon/message.png";
import firstnameIcon from "assets/img/icon/firstname.png";
import emailIcon from "assets/img/icon/email.png";
import phonenumberIcon from "assets/img/icon/phonenumber.png";
import { useTranslation } from "react-i18next";
import { useLanguage } from "components/Homepage/LanguageContext";

const ContactForm = () => {
  const { t } = useTranslation();
  const { changeLanguage } = useLanguage();

  // State to manage form input values
  const [formData, setFormData] = useState({
    name: "",
    last_name: "",
    tel: "",
    email: "",
    message: "",
  });

  // State to manage form submission status
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Handle input change
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Scroll down animation
  const sections = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );

    sections.current.forEach((section) => {
      if (section && section instanceof Element) {
        observer.observe(section);
      }
    });

    const handleScroll = debounce(() => {
      sections.current.forEach((section) => {
        if (section && section instanceof Element) {
          observer.observe(section);
        }
      });
    }, 100);

    window.addEventListener("scroll", handleScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  function debounce(func, delay) {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), delay);
    };
  }

  const form = useRef();

  const sendEmail = (e) => {
    e.preventDefault();

    emailjs
      .sendForm("service_re0yn5q", "template_zxt4p7q", form.current, {
        publicKey: "klAgwhrXKwJEHK3q7",
      })
      .then(
        () => {
          console.log("SUCCESS!");
          form.current.reset(); // Reset the form fields
          setFormData({
            name: "",
            last_name: "",
            tel: "",
            email: "",
            message: "",
          }); // Reset form data state
          setIsSubmitted(true); // Set submission status to true

          // Reset form view after 3 seconds
          setTimeout(() => {
            setIsSubmitted(false);
          }, 3000);
        },
        (error) => {
          console.log("FAILED...", error.text);
        }
      );
  };

  return (
    <div
      ref={(el) => sections.current.push(el)}
      className="animate-on-scroll bg-light-sky rounded-xl xl:p-8 lg:p-8 md:p-8 p-8 xl:ml-8 md:mt-0 mt-4"
    >
      <p className="xl:pt-2 lg:pt-0 xl:text-head11 text-blue-dark lg:text-xl md:text-lg text-lg font-semibold text-redd lg:text-xl md:text-lg text-lg banner-font font-medium">
        {t("homecontent38")}
      </p>
      <p className="pt-1 banner-font xl:text-para text-greyy">
        {t("homecontent39")}
      </p>

      {isSubmitted ? (
        <div className="thank-you-message">
          <p className="text-black font-semibold">Thank you for submitting!</p>
        </div>
      ) : (
        <form ref={form} onSubmit={sendEmail}>
          <div className="grid md:grid-cols-2 gap-2 grid-cols-1">
            <div className="input-container">
              <img src={firstnameIcon} alt="user icon" className="input-icon" />
              <input
                type="text"
                name="name"
                placeholder={t("homecontent40")}
                value={formData.name}
                onChange={handleChange}
                className="w-full mt-8 bg-white cursor-pointer rounded-lg border-white pl-10 focus:outline-none"
                required
              />
            </div>
            <div className="input-container">
              <img
                src={firstnameIcon}
                alt="user icon"
                className="input-icon1"
              />
              <input
                type="text"
                name="last_name"
                placeholder={t("homecontent41")}
                value={formData.last_name}
                onChange={handleChange}
                className="w-full md:mt-8 mt-2 bg-white cursor-pointer rounded-lg border-white pl-10 focus:outline-none"
                required
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-2 -mt-4 grid-cols-1">
            <div className="input-container">
              <img
                src={phonenumberIcon}
                alt="user icon"
                className="input-icon"
              />
              <input
                type="text"
                name="tel"
                placeholder={t("homecontent42")}
                value={formData.tel}
                onChange={handleChange}
                className="w-full mt-8 bg-white cursor-pointer rounded-lg border-white pl-10 focus:outline-none"
                required
              />
            </div>
            <div className="input-container">
              <img src={emailIcon} alt="user icon" className="input-icon22" />
              <input
                type="text"
                name="email"
                placeholder={t("homecontent43")}
                value={formData.email}
                onChange={handleChange}
                className="w-full md:mt-8 mt-2 bg-white cursor-pointer rounded-lg border-white pl-10 focus:outline-none"
                required
              />
            </div>
          </div>
          {/* desktop */}
          <div className="xl:block md:hidden lg:hidden hidden">
            <div className="input-container">
              <img src={messageIcon} alt="user icon" className="input-icon44" />
              <textarea
                placeholder={t("homecontent44")}
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="w-full mt-4 pl-10 xl:pt-2 bg-white xl:h-32  cursor-pointer rounded-lg border-white focus:outline-none"
                required
              />
            </div>
          </div>

          {/* mobile and tab  */}
          <div className="xl:hidden lg:block md:block block">
            <div className="input-container xl:mt-0 mt-4">
              <img src={messageIcon} alt="user icon" className="input-icon4" />
              <textarea
                placeholder={t("homecontent44")}
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="w-full mt-0 pl-10 xl:pt-2 bg-white h-24 lg:h-24 cursor-pointer rounded-lg border-white focus:outline-none"
                required
              />
            </div>
          </div>

          <button className="xl:mt-4 lg:mt-4 mt-4  uppercase text-blue-dark border-dark-blue  text-xs banner-font px-8  py-2">
            {t("homecontent45")}
          </button>
        </form>
      )}
    </div>
  );
};

export default ContactForm;
